/**
 * Lift2Fit — Manage coach · Discover tab (migration 0020).
 *
 * The lifter's front door to public coaches. Search by coach name, place or
 * pack; filter by kind; open a coach's public listing; then subscribe to a pack
 * — or, when not yet linked, ask to connect through the existing invite flow.
 *
 * A pack subscription needs an ACTIVE coach link. This screen never creates one
 * silently: an unlinked lifter is shown the API's own refusal (which names the
 * coach and the invite) and can send the invite from here. The one-coach rule
 * is the API's, and its refusal is rendered word for word.
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, COACH_PACK_KINDS, type CoachPack, type CoachPackKind, type CoachPackSubscription, type CoachPublicListing } from '@/lib/api';
import { Button } from '@/components/ui';
import { cn } from '@/lib/format';
import { LiveMessage, Panel, StatusChip } from '../components';
import { formatMoney } from '../crm/shared';
import { useCoaching } from '../coach';
const KIND_LABELS: Record<CoachPackKind, string> = {
  online: 'Online',
  in_person: 'In person',
  semi_coaching: 'Semi-coaching',
};

const CADENCE_LABELS: Record<string, string> = {
  one_off: 'one-off',
  weekly: 'per week',
  monthly: 'per month',
  pay_as_go: 'per hour (pay as you go)',
};

/** A keyless map link: OpenStreetMap search, no SDK and no API key. */
function mapSearchUrl(address: string): string {
  return `https://www.openstreetmap.org/search?query=${encodeURIComponent(address)}`;
}

/**
 * The rating, always carrying its number — colour and an icon are never the
 * only signal. Absent entirely when the coach has not published reviews.
 */
function RatingLine({ rating }: { rating: { average: number; count: number } }) {
  return (
    <span className="text-[12px] text-ink" data-testid="discover-rating">
      <span className="font-bold">{rating.average.toFixed(2)} / 5</span> · {rating.count}{' '}
      {rating.count === 1 ? 'review' : 'reviews'}
    </span>
  );
}

type Tone = 'info' | 'success' | 'danger';

export function DiscoverTab() {
  const coaching = useCoaching();
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState<CoachPackKind | ''>('');
  const [results, setResults] = useState<CoachPublicListing[]>([]);
  const [total, setTotal] = useState(0);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [profile, setProfile] = useState<CoachPublicListing | null>(null);
  const [subscription, setSubscription] = useState<CoachPackSubscription | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [tone, setTone] = useState<Tone>('info');
  const [busy, setBusy] = useState<string | null>(null);
  const [requested, setRequested] = useState<{ coachId: string } | null>(null);
  const [requestOpen, setRequestOpen] = useState<string | null>(null);
  // The caller's own review of the coach whose profile is open, if any.
  const [myReview, setMyReview] = useState<{ rating: number; comment: string } | null>(null);
  const [reviewBusy, setReviewBusy] = useState(false);

  const loadMyReview = useCallback(async (coachId: string) => {
    const result = await api.lifterReviews();
    const found = (result.data ?? []).find((review) => review.coachId === coachId) ?? null;
    setMyReview(found ? { rating: found.rating, comment: found.comment ?? '' } : null);
  }, []);

  const link = coaching.link;
  const linkedCoachId = link?.status === 'active' ? link.coach?.userId ?? null : null;

  const loadSubscription = useCallback(async () => {
    const result = await api.packSubscriptions();
    if (result.data) {
      setSubscription(result.data.data.find((entry) => entry.status === 'active') ?? null);
    }
  }, []);

  useEffect(() => {
    void loadSubscription();
  }, [loadSubscription]);

  const search = async (event?: FormEvent) => {
    event?.preventDefault();
    setSearching(true);
    setMessage(null);
    const result = await api.discoverCoaches({ q: query, kind: kind === '' ? null : kind });
    setSearching(false);
    setSearched(true);
    if (result.problem || !result.data) {
      setResults([]);
      setTotal(0);
      setTone('danger');
      setMessage(result.problem?.detail ?? 'Discovery could not be read.');
      return;
    }
    setResults(result.data.data);
    setTotal(result.data.total);
    setProfile(null);
  };

  const openProfile = async (coachId: string) => {
    setBusy(coachId);
    const result = await api.discoverCoach(coachId);
    setBusy(null);
    if (result.problem || !result.data) {
      setTone('danger');
      setMessage(result.problem?.detail ?? 'That profile could not be opened.');
      return;
    }
    setProfile(result.data);
    setMessage(null);
    if (result.data) await loadMyReview(result.data.coachId);
  };

  const saveReview = async (coachId: string, event: FormEvent) => {
    event.preventDefault();
    if (!myReview) return;
    setReviewBusy(true);
    setTone('info');
    setMessage('Saving your review…');
    const result = await api.saveReview(coachId, {
      rating: myReview.rating,
      comment: myReview.comment.trim() === '' ? null : myReview.comment.trim(),
    });
    setReviewBusy(false);
    if (result.problem || !result.data) {
      setTone('danger');
      setMessage(result.problem?.detail ?? 'Your review did not save.');
      return;
    }
    setMyReview({ rating: result.data.rating, comment: result.data.comment ?? '' });
    setTone('success');
    setMessage('Your review is saved. You can edit it at any time — it stays one review.');
  };

  const subscribe = async (pack: CoachPack) => {
    setBusy(pack.packId);
    setTone('info');
    setMessage(`Subscribing to ${pack.name}…`);
    const result = await api.subscribePack(pack.packId);
    setBusy(null);
    if (result.problem || !result.data) {
      setTone('danger');
      setMessage(result.problem?.detail ?? 'That pack was not subscribed to.');
      return;
    }
    setTone('success');
    setMessage(
      result.data.paymentId === null
        ? `Subscribed to ${result.data.subscription.packName}. This pack is pay-as-you-go: booking a session raises its hourly charge on your coach's ledger.`
        : `Subscribed to ${result.data.subscription.packName}. ` +
            'A due payment for it is now on your coach’s ledger — booking its slots is open to you.',
    );
    await loadSubscription();
  };

  const requestConnect = async (listing: CoachPublicListing, event: FormEvent) => {
    event.preventDefault();
    setBusy(listing.coachId);
    setTone('info');
    setMessage(`Asking ${listing.displayName} to connect…`);
    const result = await api.inviteCoach({ coachId: listing.coachId, name: listing.displayName });
    setBusy(null);
    if (result.problem || !result.data) {
      setTone('danger');
      setMessage(result.problem?.detail ?? 'That request could not be created.');
      return;
    }
    setRequested({ coachId: listing.coachId });
    setTone('success');
    setMessage(
      `Request sent to ${listing.displayName}. It is waiting in their workspace — they accept or decline with a message, and you will read the answer here.`,
    );
    coaching.reload();
  };

  const packCard = (pack: CoachPack, blockedByOtherCoach: boolean) => {
    const subscribed = subscription?.packId === pack.packId && subscription.status === 'active';
    // 0028: buying follows connecting — a lifter connected to another coach
    // cannot buy here. Explain why instead of offering a button that 409s.
    return (
      <li
        key={pack.packId}
        data-testid={`discover-pack-${pack.packId}`}
        className="flex flex-col gap-2 border border-hairline px-3 py-3"
      >
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="flex min-w-0 flex-col gap-1">
            <span className="flex flex-wrap items-center gap-2 text-[14px] font-bold text-ink">
              {pack.name}
              <StatusChip tone="info">{KIND_LABELS[pack.kind]}</StatusChip>
            </span>
            <span className="text-[12px] text-ink-muted">
              {formatMoney(pack.price, pack.currency)} {CADENCE_LABELS[pack.cadence] ?? ''} · {pack.capacity}{' '}
              {pack.capacity === 1 ? 'person' : 'people'}
              {pack.coachCount > 1 ? ` · ${pack.coachCount} coaches` : ''}
              {pack.location ? ` · ${pack.location}` : ''}
            </span>
            {pack.summary ? (
              <span className="text-[12px] text-ink" data-testid={`discover-pack-summary-${pack.packId}`}>
                {pack.summary}
              </span>
            ) : null}
            {pack.description ? <span className="text-[12px] text-ink-muted">{pack.description}</span> : null}
            {pack.inclusions.length ? (
              <span className="text-[12px] text-ink-muted">Includes {pack.inclusions.join(', ')}.</span>
            ) : null}
          </div>
          {subscribed ? (
            <StatusChip tone="success">Subscribed</StatusChip>
          ) : subscription ? (
            <StatusChip tone="neutral">On {subscription.packName}</StatusChip>
          ) : null}
        </div>
        {subscribed ? null : blockedByOtherCoach ? (
          <p className="text-[12px] text-ink-muted" data-testid={`discover-pack-blocked-${pack.packId}`}>
            You are connected to another coach. You can keep chatting, but buying a pack here opens once you
            disconnect from them — a lifter buys from one coach at a time.
          </p>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              disabled={busy === pack.packId}
              data-testid={`discover-subscribe-${pack.packId}`}
              onClick={() => void subscribe(pack)}
            >
              {busy === pack.packId ? 'Subscribing…' : `Subscribe — ${formatMoney(pack.price, pack.currency)}`}
            </Button>
          </div>
        )}
      </li>
    );
  };

  const profileView = profile ? (
    <Panel
      testId="discover-profile"
      title={profile.displayName}
      subtitle={profile.location ? `${profile.location} · public listing` : 'Public listing'}
      actions={
        <Button size="sm" variant="ghost" onClick={() => setProfile(null)}>
          Close
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {profile.rating ? <RatingLine rating={profile.rating} /> : null}
          {profile.yearsCoaching !== null ? (
            <StatusChip tone="neutral">
              {profile.yearsCoaching} {profile.yearsCoaching === 1 ? 'year' : 'years'} coaching
            </StatusChip>
          ) : null}
          {profile.specialities.map((speciality) => (
            <StatusChip key={speciality} tone="neutral">
              {speciality}
            </StatusChip>
          ))}
          {profile.languages.map((language) => (
            <StatusChip key={language} tone="neutral">
              {language}
            </StatusChip>
          ))}
        </div>
        {profile.bio ? <p className="text-[13px] text-ink-muted">{profile.bio}</p> : null}

        {/* Locations: a plain address list with a keyless OpenStreetMap link.
            Degrades to a single "no location" line when the coach has none. */}
        <div className="flex flex-col gap-2" data-testid="discover-locations">
          <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Where to find them</span>
          {profile.locations.length === 0 && !profile.location ? (
            <p className="text-[12px] text-ink-muted" data-testid="discover-locations-empty">
              This coach has not published a location.
            </p>
          ) : profile.locations.length > 0 ? (
            <ul className="flex flex-col gap-1">
              {profile.locations.map((place) => (
                <li key={place.locationId} className="flex flex-wrap items-center gap-2 text-[13px] text-ink">
                  <span>
                    {place.label ? <span className="font-bold">{place.label}: </span> : null}
                    {place.address}
                  </span>
                  <a
                    href={mapSearchUrl(place.address)}
                    target="_blank"
                    rel="noreferrer noopener"
                    data-testid={`discover-map-${place.locationId}`}
                    className="text-[12px] font-bold uppercase tracking-[0.06em] text-accent hover:text-accent-hover"
                  >
                    View on map
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <p className="flex flex-wrap items-center gap-2 text-[13px] text-ink">
              {profile.location}
              <a
                href={mapSearchUrl(profile.location ?? '')}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="discover-map-primary"
                className="text-[12px] font-bold uppercase tracking-[0.06em] text-accent hover:text-accent-hover"
              >
                View on map
              </a>
            </p>
          )}
        </div>

        {profile.contact ? (
          <p className="text-[13px] text-ink" data-testid="discover-profile-contact">
            <span className="font-bold uppercase tracking-[0.06em] text-ink-muted">
              {profile.contact.kind}
            </span>{' '}
            {profile.contact.value}
          </p>
        ) : (
          <p className="text-[12px] text-ink-muted">This coach has not published a contact method.</p>
        )}

        {profile.packs.length === 0 ? (
          <p className="text-[13px] text-ink-muted">No packs published yet.</p>
        ) : (
          <ul className="flex flex-col gap-2" data-testid="discover-profile-packs">
            {profile.packs.map((pack) => packCard(pack, !profile.connectable))}
          </ul>
        )}

        <div className="border-t border-hairline pt-3">
          {linkedCoachId === null || linkedCoachId !== profile.coachId ? (
            requestOpen === profile.coachId ? (
              <form
                className="flex flex-col gap-2"
                onSubmit={(event) => void requestConnect(profile, event)}
                data-testid="discover-request-form"
              >
                <p className="text-[12px] text-ink-muted">
                  {linkedCoachId === null
                    ? `Send ${profile.displayName} a connect request — they accept it from their workspace, and the chat opens straight away.`
                    : `You are connected to another coach, so ${profile.displayName} cannot connect you yet. You can still send this request and chat now; connecting and buying open once you disconnect.`}
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="submit"
                    size="sm"
                    variant="secondary"
                    disabled={busy === profile.coachId}
                    data-testid="discover-request-submit"
                  >
                    Send connect request
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setRequestOpen(null)}>
                    Not now
                  </Button>
                </div>
              </form>
            ) : (
              <div className="flex flex-col gap-2">
                {!profile.connectable ? (
                  <p
                    className="border-l-2 border-l-warning bg-page-subtle px-3 py-2 text-[13px] text-ink"
                    data-testid="discover-other-coach-note"
                  >
                    You are connected to another coach. You can chat with {profile.displayName} now; connecting and
                    buying a pack open once you disconnect — a lifter connects to (and buys from) one coach at a time.
                  </p>
                ) : null}
                <div>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid="discover-request"
                    onClick={() => setRequestOpen(profile.coachId)}
                  >
                    {profile.connectable ? 'Request to connect' : 'Start a chat / request to connect'}
                  </Button>
                </div>
              </div>
            )
          ) : linkedCoachId === profile.coachId ? (
            <div className="flex flex-col gap-3">
              <p className="text-[12px] text-ink-muted" data-testid="discover-linked-note">
                You are linked to {profile.displayName}. Subscribing here is immediate.
              </p>
              {/* You have trained with this coach, so you may review them. One
                  review per coach: leaving another edits this one. */}
              {myReview === null ? (
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="discover-review-start"
                  onClick={() => setMyReview({ rating: 5, comment: '' })}
                >
                  Rate this coach
                </Button>
              ) : (
                <form
                  className="flex flex-col gap-2 border border-hairline p-3"
                  onSubmit={(event) => void saveReview(profile.coachId, event)}
                  data-testid="discover-review-form"
                >
                  <p className="text-[12px] text-ink-muted">
                    One review per coach. The rating always carries its number.
                  </p>
                  <label className="flex flex-wrap items-center gap-2 text-[13px] text-ink">
                    <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">
                      Rating
                    </span>
                    <select
                      data-testid="discover-review-rating"
                      value={myReview.rating}
                      onChange={(event) =>
                        setMyReview({ ...myReview, rating: Number(event.target.value) })
                      }
                      className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
                    >
                      {[5, 4, 3, 2, 1].map((value) => (
                        <option key={value} value={value}>
                          {value} / 5
                        </option>
                      ))}
                    </select>
                  </label>
                  <input
                    type="text"
                    data-testid="discover-review-comment"
                    value={myReview.comment}
                    onChange={(event) => setMyReview({ ...myReview, comment: event.target.value })}
                    placeholder="What was the coaching like? (optional)"
                    className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
                  />
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="submit"
                      size="sm"
                      variant="secondary"
                      disabled={reviewBusy}
                      data-testid="discover-review-submit"
                    >
                      {reviewBusy ? 'Saving…' : 'Save review'}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setMyReview(null)}>
                      Close
                    </Button>
                  </div>
                </form>
              )}
            </div>
          ) : (
            <p className="border-l-2 border-warning bg-page-subtle px-3 py-2 text-[13px] text-ink" data-testid="discover-other-coach-note">
              You already have a coach. A pack from {profile.displayName} is refused until you revoke the current link.
            </p>
          )}
          {requested?.coachId === profile.coachId ? (
            <p className="mt-2 text-[13px] text-ink" data-testid="discover-request-sent">
              Request sent to {profile.displayName}. It is waiting in their workspace — they accept or decline with a
              message, and you will read the answer on the Coach tab. No code to pass on: you asked them, not the other
              way round.
            </p>
          ) : null}
        </div>
      </div>
    </Panel>
  ) : null;

  return (
    <div className="flex flex-col gap-4" data-testid="my-coach-discover-tab">
      {subscription ? (
        <Panel
          testId="discover-my-pack"
          title="Your pack"
          subtitle="Slots are pack-scoped: you can only book sessions for the pack you hold."
          actions={<StatusChip tone="success">Active</StatusChip>}
        >
          <p className="text-[13px] text-ink">
            <span className="font-bold">{subscription.packName}</span> with {subscription.coachName} ·{' '}
            {formatMoney(subscription.price, subscription.currency)} {CADENCE_LABELS[subscription.cadence] ?? ''}
          </p>
        </Panel>
      ) : null}

      <Panel
        title="Find a coach"
        subtitle="Search published coaches by name, place or pack. Only coaches who opted in appear — and only what they chose to publish."
      >
        <form className="flex flex-wrap items-end gap-2" onSubmit={(event) => void search(event)} data-testid="discover-search-form">
          <label className="flex min-w-[220px] flex-1 flex-col gap-1.5">
            <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Search</span>
            <input
              type="search"
              data-testid="discover-search-input"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Coach, place or pack"
              className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Kind</span>
            <select
              data-testid="discover-kind-filter"
              value={kind}
              onChange={(event) => setKind(event.target.value as CoachPackKind | '')}
              className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
            >
              <option value="">Any kind</option>
              {COACH_PACK_KINDS.map((entry) => (
                <option key={entry} value={entry}>
                  {KIND_LABELS[entry]}
                </option>
              ))}
            </select>
          </label>
          <Button type="submit" disabled={searching} data-testid="discover-search-submit">
            {searching ? 'Searching…' : 'Search'}
          </Button>
        </form>

        <LiveMessage message={message} tone={tone} />

        {searched ? (
          results.length === 0 ? (
            <p className="text-[13px] text-ink-muted" data-testid="discover-no-results">
              No published coach matches that search.
            </p>
          ) : (
            <>
              <p className="text-[12px] text-ink-muted" data-testid="discover-count">
                {total} {total === 1 ? 'coach' : 'coaches'} published
              </p>
              <ul className="flex flex-col gap-2" data-testid="discover-results">
                {results.map((listing) => (
                  <li
                    key={listing.coachId}
                    data-testid={`discover-coach-${listing.coachId}`}
                    className="flex flex-wrap items-start justify-between gap-2 border border-hairline px-3 py-3"
                  >
                    <div className="flex min-w-0 flex-col gap-1">
                      <span className="text-[15px] font-bold text-ink">{listing.displayName}</span>
                      <span className="text-[12px] text-ink-muted">
                        {listing.locations.length > 0
                          ? listing.locations.map((place) => place.address).join(' · ')
                          : (listing.location ?? 'No location published')}
                        {listing.specialities.length ? ` · ${listing.specialities.join(', ')}` : ''}
                        {listing.yearsCoaching !== null ? ` · ${listing.yearsCoaching} yrs` : ''}
                      </span>
                      {/* Nothing about ratings when the coach has not published
                          them — the API omits the field entirely. */}
                      {listing.rating ? <RatingLine rating={listing.rating} /> : null}
                      <span className="text-[12px] text-ink-muted">
                        {listing.packs.length} {listing.packs.length === 1 ? 'pack' : 'packs'}:{' '}
                        {listing.packs.map((pack) => pack.name).join(', ') || 'none yet'}
                      </span>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy === listing.coachId}
                      data-testid={`discover-coach-open-${listing.coachId}`}
                      onClick={() => void openProfile(listing.coachId)}
                    >
                      View profile
                    </Button>
                  </li>
                ))}
              </ul>
            </>
          )
        ) : (
          <p className="text-[12px] text-ink-muted" data-testid="discover-prompt">
            Search for a coach or a pack to see who is published.
          </p>
        )}
      </Panel>

      {profileView}

      <p className={cn('text-[12px] text-ink-muted')}>
        Discovery reads public profiles and packs only. A coach never sees you until you connect, and you can revoke at
        any time.
      </p>
    </div>
  );
}
